/* eslint-disable no-unused-vars */
/* eslint-disable no-case-declarations */
/* eslint-disable max-lines */
import {
    NonLLMStep,
    Opcode,
    OpcodeStep,
    OpcodeStepWithIdentification,
    StepLoop,
    StepType
} from 'builders/builderOpcode/types/newOpcodeTypes.types';
import {
    operatorOptions
} from '../context/OpCodeBuilderContext';

export interface StepOutput {
    label: string
    outputName: string
    type?: string
}

type LastOutputValue = {
    stepType: StepType
    stepIndex: number
    stepId?: string
    outputs: StepOutput[]
};

type NextStep = {
    stepIndex: number;
    nextStep: {
        value: string;
        label: string;
    };
}

class OpCodeBuilderHandler {
    validateIndexName(value: string, name: string, stepIndex: number, opCodeSteps: OpcodeStep[] = []) {
        if (!value) {
            return {
                isValid: false,
                message: `${name} name field cannot be empty.`
            };
        }
        if (/\s/.test(value)) {
            return {
                isValid: false,
                message: `${name} name cannot contain spaces.`
            };
        }
        if (/^_/.test(value)) {
            return {
                isValid: false,
                message: `${name} name cannot start with an underscore.`
            };
        }
        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(value)) {
            return {
                isValid: false,
                message: `${name} name can only contain letters, numbers, and underscores, and must not start with a number or underscore.`
            };
        }
        if (value.length < 4) {
            return {
                isValid: false,
                message: `${name} name should be at least 4 characters long.`
            };
        }

        // check if the output name is already used in later steps
        if (this.isIndexNameUsedInOtherSteps(opCodeSteps, stepIndex, name, value)) {
            return {
                isValid: false,
                message: 'this name is used in other step or fields.'
            };
        }

        return {
            isValid: true,
            message: `Valid ${name} name.`
        };
    }

    isIndexNameUsedInOtherSteps(opCodeSteps: OpcodeStep[], index: number, name: string, value: string) {
        if (value === '') return false;

        return opCodeSteps.some((opCoStep, i) => {
            if (opCoStep.step_type === StepType.Loop) {
                if ((opCoStep?.input_params?.index === value && (name !== 'index' || i !== index) || opCoStep?.input_params?.iterator === value && (name !== 'iterator' || i !== index))) {
                    return true;
                }

                // TODO: enable this when nested loop is enabled
                // // check nested steps
                // if (this.isIndexNameUsedInOtherSteps(opCoStep.steps, i, value)) {
                //     return true;
                // }
                return false;
            }

            return false;
        });
    }

    extractOutputValues = (text: string) => {
        const regex = /output\[['"]([a-zA-Z0-9_]+)['"]\]/g;
        let matches: RegExpExecArray | null;
        const result = [];

        while ((matches = regex.exec(text)) !== null) {
            const value = matches[1];
            if (!result.includes(value)) {
                result.push(value);
            }
        }

        return result;
    };

    getContextBasedOnStepType(opCoStep: OpcodeStep) {
        switch (opCoStep.step_type) {
            case StepType.Identification:
                return opCoStep?.input_params?.query || '';
            case StepType.LLM:
                return [
                    opCoStep?.input_params?.query || '',
                    opCoStep?.input_params?.image_urls || '',
                ];
            case StepType.API:
                const contextKeysArr = [];
                const body = opCoStep.input_params?.body || {};
                const query_params = opCoStep.input_params?.query_params || {};
                const contextKeys = {
                    ...body,
                    ...query_params
                };

                if (Object.keys(contextKeys).length > 0) {
                    for (const key in contextKeys) {
                        contextKeysArr.push(contextKeys[key]);
                    }
                }
                return contextKeysArr;
            case StepType.NonLLM:
                return [
                    opCoStep?.input_params?.function_call?.function_params?.query_text || '',
                    opCoStep?.input_params?.function_call?.function_params?.offset || '',
                    opCoStep?.input_params?.function_call?.function_params?.file_keys_filter || '',
                    opCoStep?.input_params?.function_call?.function_params?.collection_id || '',
                ];
                case StepType.Loop:
                const commonValues: any[] = [
                    opCoStep?.input_params?.continue_condition || '',
                    opCoStep?.input_params?.break_condition || '',
                ];
                if (opCoStep?.input_params?.mode === 'collection') {
                    return commonValues.push(...[
                        opCoStep?.input_params?.collection || '',
                        opCoStep?.input_params?.collection_type || '',
                    ]);
                } else {
                    return commonValues.push(...[
                        opCoStep?.input_params?.range_end || '',
                        opCoStep?.input_params?.range_start || '',
                        opCoStep?.input_params?.range_step || '',
                    ]);
                }
            case StepType.Condition:
                return [
                    opCoStep?.input_params?.expression || ''
                ];
            default:
                return '';
        }
    }

    extractContextKeys = (opCodeStep: OpcodeStep) => {
        const contextKeys = new Set();
        const regex = /context\[['"]([a-zA-Z0-9_]+)['"]\]/g;

        const crawl = (obj) => {
            if (!obj) return;

            if (typeof obj === 'string') {
                let matches: RegExpExecArray | null;
                while ((matches = regex.exec(obj)) !== null) {
                    contextKeys.add(matches[1]);
                }
                return;
            }

            if (Array.isArray(obj)) {
                obj.forEach(item => crawl(item));
                return;
            }

            if (typeof obj === 'object') {
                Object.values(obj).forEach(value => crawl(value));
                return;
            }
        };

        crawl(opCodeStep);
        return Array.from(contextKeys);
    };

    isContextKeyUsedInOtherSteps = (opCodeSteps: OpcodeStep[], keyToRemove: string) => {
        return opCodeSteps.some((opCoStep) => {
            if (opCoStep.step_type === StepType.LLM || opCoStep.step_type === StepType.Identification) {
                return this.extractContextKeys(opCoStep).find(key => key === keyToRemove);
            }

            if (opCoStep.step_type === StepType.NonLLM || opCoStep.step_type === StepType.API) {
                return this.extractContextKeys(opCoStep).find(key => key === keyToRemove);
            }

            return false;
        });
    };

    _formatKey(parentKey: string, key: string, prefix: boolean) {
        if (parentKey) {
            return prefix ? `${parentKey}.${key}` : `${parentKey}_${key}`;
        }
        return key;
    }

    getLastOutputValue = (opCoStep: OpcodeStep, stepIndex: number): LastOutputValue | null => {
        switch (opCoStep.step_type) {
            case StepType.LLM:
                // check if output type is dict and output schema is defined
                if (opCoStep.output_params?.output_type === 'dict' && (opCoStep.output_params?.output_schema && typeof opCoStep.output_params.output_schema === 'object')) {
                    return {
                        stepIndex: stepIndex,
                        stepType: StepType.LLM,
                        outputs: this.getSchemaKeys(opCoStep.output_params.output_schema, opCoStep.output_params.output_name, false),
                    };
                }
                return {
                    stepIndex: stepIndex,
                    stepType: StepType.LLM,
                    outputs: [
                        {
                            label: 'LLM Output',
                            outputName: opCoStep?.output_params?.output_name || '',
                            type: opCoStep?.output_params.output_type || 'string'
                        }
                    ]
                };
            case StepType.Memory:
                return {
                    stepIndex: stepIndex,
                    stepType: StepType.Memory,
                    outputs: [
                        {
                            label: 'Memory Output',
                            outputName: opCoStep?.output_params?.output_name || '',
                            type: 'string'
                        }
                    ]
                };
            case StepType.NonLLM:
                return {
                    stepIndex: stepIndex,
                    stepType: StepType.NonLLM,
                    outputs: [
                        {
                            label: 'Non LLM compiled text output',
                            outputName: opCoStep?.output_params._compiled_text,
                            type: 'list'
                        },
                        {
                            label: 'Non LLM all fields output',
                            outputName: opCoStep?.output_params._all_fields,
                            type: 'list'
                        },
                        {
                            label: 'Non LLM presigned URLs output',
                            outputName: opCoStep?.output_params._presigned_urls,
                            type: 'list'
                        },
                        {
                            label: 'Non LLM total results output',
                            outputName: opCoStep?.output_params._total_results,
                            type: 'number'
                        },
                    ],
                };
            case StepType.API:
                const output_schema = opCoStep.input_params?.output_schema || {};
                if (opCoStep.output_params?._compiled_output === true) {
                    return {
                        stepIndex: stepIndex,
                        stepType: StepType.API,
                        outputs: [{
                            label: 'API Output',
                            outputName: opCoStep.output_params._output,
                            type: 'object'
                        }],
                    };
                } else {
                    return {
                        stepIndex: stepIndex,
                        stepType: StepType.API,
                        outputs: this.getSchemaKeys(output_schema, opCoStep?.output_params?._output, false),
                    };
                }
            case StepType.Loop:
                const loopOutput = opCoStep?.output_params?.output_name || '';
                const nestedOutput = opCoStep.steps.map((nestedStep, nestedIndex) => this.getLastOutputValue(nestedStep, nestedIndex)).filter(Boolean);
                return {
                    stepIndex: stepIndex,
                    stepType: StepType.Loop,
                    outputs: [
                        {
                            label: 'Loop Output',
                            outputName: loopOutput,
                            type: 'string'
                        },
                        ...nestedOutput.flatMap(value => {
                            return value!.outputs;
                        })
                    ]
                };
            default:
                return null;
        }
    };

    getPreviousOutputs = (opCodeSteps: OpcodeStep[], currentIndex: number): LastOutputValue[] => {
        if (currentIndex <= 0) return [];
        return opCodeSteps
            .slice(0, currentIndex)
            .map((opCoStep, i) => this.getLastOutputValue(opCoStep, i)).filter(Boolean);
    };

    getSchemaKeys(schema: any, parentKey = '', prefix = false, parentName = ''): StepOutput[] {
        const keys: StepOutput[] = [];

        if (!schema || schema.type !== 'object' || !schema.properties) {
            return keys;
        }

        for (const [key, value] of Object.entries(schema.properties)) {
            const fullKey = this._formatKey(parentKey, key, prefix);

            if (
                typeof value === 'object' &&
                value !== null &&
                'type' in value &&
                (value as { type?: string }).type === 'object' &&
                'properties' in value
            ) {
                keys.push({
                    label: `Api output Object: ${key}`,
                    outputName: fullKey,
                    type: (value as any).type
                });

                // Recursively push child properties
                keys.push(...this.getSchemaKeys(value, fullKey, true, key));
            } else {
                keys.push({
                    label: `Api output Field: ${parentName ? parentName + '.' + key : key}`,
                    outputName: fullKey,
                    type: (value as any).type || 'unknown'
                });
            }
        }

        return keys;
    }

    getNestedOutputValues(steps: OpcodeStep[]) {
        return steps.flatMap((step, index) => {
            return this.getLastOutputValue(step, index).outputs.map((value) => {
                if (value) {
                    return value.outputName;
                }
                return '';
            }).filter(value => value.trim() !== '');
        });
    }

    // eslint-disable-next-line no-unused-vars
    getNextSteps = (opCodeSteps: OpcodeStep[], currentIndex: number): NextStep[] => {
        // Filter out identification steps for numbering
        const workflowSteps = opCodeSteps.filter(step => step.step_type !== StepType.Identification);
        return opCodeSteps
            .filter((opCoStep, index) => index !== currentIndex && opCoStep.step_type !== StepType.Identification)
            .map((opCoStep) => {
                // Find the index in workflowSteps (excluding identification)
                const workflowIndex = workflowSteps.findIndex(s => s.step_id === opCoStep.step_id);
                return {
                    stepIndex: workflowIndex + 1,
                    nextStep: {
                        value: opCoStep.step_id,
                        label: `Step ${workflowIndex + 1}`,
                    }
                };
            });
    };

    removeSpecificContextFromOtherSteps = (setOpCodeSteps: React.Dispatch<React.SetStateAction<OpcodeStep[]>>, contextToRemove: string) => {
        const regex = new RegExp(`\\{\\{context\\['${contextToRemove}'\\]\\}}`, 'g');

        setOpCodeSteps((prev) => prev.map((opCoStep) => {
            if (opCoStep.step_type === StepType.NonLLM) {
                const query_text = opCoStep.input_params?.function_call?.function_params?.query_text || '';
                const offset = opCoStep.input_params?.function_call?.function_params?.offset || '';
                const file_keys_filter = opCoStep.input_params?.function_call?.function_params?.file_keys_filter || '';

                if (query_text && typeof query_text === 'string') {
                    opCoStep.input_params.function_call.function_params.query_text = query_text.replace(regex, '');
                }
                if (offset && typeof offset === 'string') {
                    opCoStep.input_params.function_call.function_params.offset = offset.replace(regex, '');
                }
                if (file_keys_filter && typeof file_keys_filter === 'string') {
                    opCoStep.input_params.function_call.function_params.file_keys_filter = file_keys_filter.replace(regex, '');
                }

                return opCoStep;
            } else if (opCoStep.step_type === StepType.Loop) {
                if (opCoStep?.input_params?.break_condition && typeof opCoStep.input_params.break_condition === 'string') {
                    opCoStep.input_params.break_condition = opCoStep.input_params.break_condition.replace(regex, '');
                }

                return opCoStep;
            } else if (opCoStep.step_type === StepType.API) {
                // remove from body
                const bodyContext = opCoStep.input_params.body || {};
                if (Object.keys(bodyContext).length > 0) {
                    for (const key in bodyContext) {
                        const value = bodyContext[key];
                        if (typeof value === 'string') {
                            const updatedText = value.replace(regex, '');
                            if (updatedText !== value) {
                                bodyContext[key] = updatedText;
                            }
                        }
                    }
                    opCoStep.input_params.body = bodyContext;
                }

                // remove from query params
                const queryContextKeys = opCoStep.input_params.query_params || {};
                if (Object.keys(queryContextKeys).length > 0) {
                    for (const key in queryContextKeys) {
                        const value = queryContextKeys[key];
                        if (typeof value === 'string') {
                            const updatedText = value.replace(regex, '');
                            if (updatedText !== value) {
                                queryContextKeys[key] = updatedText;
                            }
                        }
                    }
                    opCoStep.input_params.query_params = queryContextKeys;

                    return opCoStep;
                }
            } else if (opCoStep.step_type !== StepType.Memory && opCoStep.step_type !== StepType.Condition) {
                if (opCoStep.input_params.query && typeof opCoStep.input_params.query === 'string') {
                    opCoStep.input_params.query = opCoStep.input_params.query.replace(regex, '');
                }
                return opCoStep;
            }

            return opCoStep;
        }));
    };

    isConditionIncomplete = (condition: string): boolean => {
        const parts = condition.split(/\s+/).filter(Boolean);

        if (parts.length < 3) {
            return true;
        }

        const [left, operator, right] = parts;
        const validOperators = operatorOptions.map(op => op.value);

        if (!left || !operator || !right) {
            return true;
        }

        if (!validOperators.includes(operator)) {
            return true;
        }

        return false;
    };

    isConditionValidate = (condition: string): boolean => {
        const parts = condition.split(/\s+/).filter(Boolean).map(str => str.replace(/^['"]|['"]$/g, ''));

        if (parts.length < 3) {
            return true;
        }

        const [left, operator, right] = parts;

        const isNumericOperator = ['>', '<', '>=', '<='].includes(operator);

        if (isNumericOperator) {
            if (Array.isArray(left) || Array.isArray(right)) return true;

            const isMixedStaticAndDynamic = (value) => {
                const dynamicOnly = value.replace(/{{[^}]+}}/g, '').trim();
                const hasDynamic = /{{[^}]+}}/.test(value);

                return hasDynamic && dynamicOnly.length > 0;
            };

            const getStaticPart = (value) => {
                return value.replace(/{{[^}]+}}/g, '').trim();
            };

            const isStaticPartNumber = (value) => {
                if (value.length === 0) return true;
                return !isNaN(Number(value));
            };

            const isLeftMixed = isMixedStaticAndDynamic(left);
            const isRightMixed = isMixedStaticAndDynamic(right);
            const isLeftStaticNumber = isStaticPartNumber(getStaticPart(left));
            const isRightStaticNumber = isStaticPartNumber(getStaticPart(right));

            if (isLeftMixed || isRightMixed || !isLeftStaticNumber || !isRightStaticNumber) return true;
        }

        return false;
    };

    checkStepDependency(stepId: string, steps: OpcodeStep[], nested: boolean) {
        try {
            const visited = new Set();
            const validSteps = new Set();

            const getNextSteps = (step: OpcodeStep) => {
                if (step.step_type === StepType.Condition) {
                    return [
                        ...(step.routing.true_branch || []),
                        ...(step.routing.false_branch || [])
                    ];
                } else {
                    return step.routing?.next || [];
                }
            };

            const markReachableSteps = (currentStepId: string) => {
                if (visited.has(currentStepId)) return;
                visited.add(currentStepId);
                validSteps.add(currentStepId);

                const currentStep = steps.find(step => step.step_id === currentStepId);
                if (!currentStep) return;

                const nextSteps = getNextSteps(currentStep);
                for (let nextStepId of nextSteps) {
                    markReachableSteps(nextStepId);
                }
            };

            // Mark steps reachable from the first two steps
            if (nested) {
                steps.slice(0, 1).forEach(step => {
                    markReachableSteps(step.step_id);
                });

            } else {
                steps.slice(0, 2).forEach(step => {
                    validSteps.add(step.step_id);
                    const nextSteps = getNextSteps(step);
                    nextSteps.forEach(markReachableSteps);
                });
            }

            // Check if the given stepId is in the set of valid steps
            return validSteps.has(stepId);
        } catch { /* empty */

        }
    }

    formatToTitleCase(str: string): string {
        return str
            .replace(/_/g, ' ') // Replace underscores with spaces
            .replace(/\w\S*/g, function(word) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }); // Convert to Title Case
    }

    removeDeletedOutput = (text: string, inputText: string): string => {
        const regex = /\{\{(.*?)\}\}/g; // Match anything inside double curly braces
        const placeholders = new Set();
        const inputTrimmed = inputText.trim();

        let output = text.replace(regex, (match, p1) => {
            const placeholder = p1.trim();

            // Check if the current placeholder matches inputText
            if (placeholder === `output['${inputTrimmed}']`) {
                return ''; // Remove this placeholder
            }

            if (placeholders.has(placeholder)) {
                return ''; // Remove duplicates
            }

            placeholders.add(placeholder);
            return match; // Keep the original match if unique
        });

        return output.replace(/\s+/g, ' ').trim(); // Remove extra spaces
    };

    generateStepNumbers(steps: OpcodeStepWithIdentification, prefix = ''): { step_id: string; step_type: StepType; step_number: string }[] {
        let result: { step_id: string; step_type: StepType; step_number: string }[] = [];

        if (Array.isArray(steps)) {
            steps?.forEach((step, index) => {
                let stepNumber = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
                result.push({
                    step_id: step.step_id,
                    step_type: step.step_type,
                    step_number: stepNumber
                });

                if (step.step_type === StepType.Loop && step.steps) {
                    result.push(...this.generateStepNumbers(step.steps as OpcodeStepWithIdentification, stepNumber));
                }
            });
        }

        return result;
    }

    getIndicatorNumber = (stepId: string, indicators: { step_id: string; step_number: string }[] = []): string => {
        const step = indicators?.find(indicator => indicator.step_id === stepId);
        return step?.step_number || '0';
    };

    ragOutputToServer(output: object): Record<string, string> {
        const newOutput: Record<string, string> = {};
        // set all _ to it's own value
        for (const key in output) {
            if (!key.startsWith('_')) continue; // Skip keys that not start with underscore

            const originalKey = key.slice(1); // Remove the leading underscore
            // find key with the same value
            newOutput[output[key] || ''] = originalKey;
        }

        return newOutput;
    }

    ragOutputFromServer(output: object): Record<string, string> {
        const newOutput: Record<string, string> = {
            _compiled_text: '',
            _all_fields: '',
            _presigned_urls: '',
            _total_results: 'total_count',
        };
        // set all _ to it's own value
        for (const key in output) {
            if (key.startsWith('_')) continue; // Skip keys that start with underscore

            newOutput[`_${output[key]}`] = key;
        }

        return newOutput;
    }

    handleInputValidation(value: string, callback: () => void) {
        const dynamicPattern = /\{\{\s*\w+\s*\[\s*['"][^'"]+['"]\s*\]\s*\}\}/;

        // check if the value is a valid context
        if (dynamicPattern.test(value)) {
            callback();
        }
    }

    modifyOpCodeDataNonLLM(step: NonLLMStep) {
        const output = step?.output_params || {};
        const newOpCodeStep = {
            ...step
        };
        newOpCodeStep.output_params.output_names = this.ragOutputToServer(output);

        return newOpCodeStep;
    }

    modifyOpCodeDataLoop(step: StepLoop) {
        const newOpCodeStep = {
            ...step
        };

        newOpCodeStep.output_params.step_output_keys = step?.steps.map((nestedStep, stepIndex) => {
            switch (nestedStep.step_type) {
                case StepType.LLM:
                    return this.generateLLMStepOutput(`${stepIndex + 1}`);
                case StepType.Memory:
                    return this.generateMemoryStepOutput(`${stepIndex + 1}`);
                case StepType.NonLLM:
                    return `s${stepIndex + 1}_non_llm_text`; // use this because generateNonLLMStepOutput generates multiple outputs
                case StepType.Condition:
                    return `s${stepIndex + 1}_condition_text`; // use this because no method to generates multiple outputs for condition
                case StepType.API:
                    return this.generateAPIStepOutput(`${stepIndex + 1}`);
            }
        });

        // recursively modify nested steps
        newOpCodeStep.steps = newOpCodeStep.steps.map((nestedStep) => {
            if (nestedStep.step_type === StepType.NonLLM) {
                return this.modifyOpCodeDataNonLLM(nestedStep);
            } else {
                return nestedStep;
            }
        });

        return newOpCodeStep;
    }

    modifyOpCodeData(opCode: Opcode): Opcode {
        const modifiedOpCode = {
            ...opCode
        };
        modifiedOpCode.steps = modifiedOpCode.steps.map((step) => {
            if (step.step_type === StepType.Loop) {
                return this.modifyOpCodeDataLoop(step);
            } else if (step.step_type === StepType.NonLLM) {
                return this.modifyOpCodeDataNonLLM(step);
            } else {
                return step;
            }
        });

        return modifiedOpCode;
    }

    hasDuplicateValues(obj: object): boolean {
        const values = Object.values(obj);
        const uniqueValues = new Set(values);
        return uniqueValues.size !== values.length;
    }

    validateSteps(steps: OpcodeStepWithIdentification) {
        let errorMessages = [];

        steps.forEach((step, index) => {
            let stepErrors = [];

            // Validate LLM steps
            if (step.step_type === StepType.LLM || step.step_type === StepType.Identification) {
                // validate query
                if (!step?.input_params.model_type) {
                    stepErrors.push('no model type');
                }
                if (!step.input_params?.unique_name) {
                    stepErrors.push('no instruction');
                }
            }

            // test_non_llm_new_schema
            // Validate Non-LLM steps
            else if (step.step_type === StepType.NonLLM) {
                if (!step?.input_params.function_call.function_name) {
                    stepErrors.push('no function selected');
                }
                if (step.input_params.function_call.function_name === 'RAG_API' && !step?.input_params.function_call.function_params?.collection_id) {
                    stepErrors.push('no collection selected');
                }
                if (!step?.input_params.function_call.function_params || Object.keys(step.input_params.function_call.function_params).length === 0) {
                    stepErrors.push('no fixed string');
                }

                // validate query not empty on vector search type
                const {
                    query_text,
                    search_type
                } = step?.input_params?.function_call?.function_params ?? {};

                if (search_type === 'vector' && query_text.trim() === '') {
                    stepErrors.push('query text is required for vector search type');
                }

                // Validate output names
                const compiled_text = step?.output_params?._compiled_text || '';
                const all_fields = step?.output_params?._all_fields || '';
                const presigned_urls = step?.output_params?._presigned_urls || '';
                const total_count = step?.output_params?._total_results || '';
                if (this.hasDuplicateValues({
                    compiled_text,
                    all_fields,
                    presigned_urls,
                    total_count
                })) {
                    stepErrors.push('duplicate output variable names');
                }

                const output = step?.output_params || {};

                for (const key in output) {
                    if (!key.startsWith('_')) continue; // Skip keys that do not start with '_'

                    if (output[key] === '') {
                        stepErrors.push(`no value for output variable: ${key}`);
                        continue; // Skip further validation for this key
                    }
                }

            }
            // Validate Condition step
            else if (step.step_type === StepType.Condition) {
                if (step?.input_params.expression.trim() === '' || handler.isConditionIncomplete(step.input_params.expression)) {
                    stepErrors.push('no expression or some parts of it are missing');
                }

                if (handler.isConditionValidate(step?.input_params.expression)) {
                    stepErrors.push("Numeric operators (>, <, ==, >=, !=, <=) can't be used with lists, strings or mixed inputs. Please change the dynamic input.");
                }

                if (step?.routing?.true_branch?.length === 0) {
                    stepErrors.push('no step selected in the true branch');
                }

                if (step?.routing?.false_branch?.length === 0) {
                    stepErrors.push('no step selected in the false branch');
                }
            }
            // Validate loop step
            else if (step.step_type === StepType.Loop) {
                if (step?.input_params?.mode === 'collection' && step?.input_params?.collection?.trim() === '') {
                    stepErrors.push('no collection name added');
                }

                if (step?.input_params?.break_condition?.trim() !== '') {
                    if (handler.isConditionIncomplete(step?.input_params?.break_condition)) {
                        stepErrors.push('some parts of break condition are missing');
                    }
                }

                if (step?.input_params?.max_iterations !== undefined) {
                    if (!step?.input_params?.max_iterations) {
                        stepErrors.push('no max iteration provided');
                    }
                }

                // check index
                if (!step?.input_params?.index) {
                    stepErrors.push('no index name provided');
                } else {
                    const {
                        isValid,
                        message
                    } = handler.validateIndexName(step?.input_params?.index, 'index', index, steps as OpcodeStep[]);
                    if (!isValid) {
                        stepErrors.push(`invalid index name: ${message}`);
                    }
                }

                // check iterator on collection mode
                if (!step?.input_params?.iterator) {
                    stepErrors.push('no iterator name provided');
                } else {
                    const {
                        isValid,
                        message
                    } = handler.validateIndexName(step?.input_params?.iterator, 'iterator', index, steps as OpcodeStep[]);
                    if (!isValid) {
                        stepErrors.push(`invalid index name: ${message}`);
                    }
                }

                const data = this.validateSteps(step?.steps as OpcodeStepWithIdentification);
                if (data?.message !== 'All steps are valid.') {
                    stepErrors.push(`Nested steps: ${data?.message}`);
                }
            }

            // Validate output for both step types
            if (step.step_type === StepType.LLM || step.step_type === StepType.Memory) {
                if (!step?.output_params?.output_name) {
                    stepErrors.push('no output variable');
                }
            }

            if (step.step_type === StepType.API) {
                if (!step?.input_params?._id) {
                    stepErrors.push('no API selected');
                }

                if (!step?.input_params?.name) {
                    stepErrors.push('no API name');
                }

                if (!step?.output_params?._output) {
                    stepErrors.push('no output name');
                }
            }

            if (stepErrors.length > 0) {
                errorMessages.push(`Step ${step.step_type === StepType.Identification ? StepType.Identification : index} has ${stepErrors.join(', ')}.`);
            }
        });

        return {
            isValid: errorMessages.length === 0,
            message: errorMessages.length > 0 ? errorMessages.join(' ') : 'All steps are valid.'
        };
    }

    generateLLMStepOutput(stepIndex: string): string {
        return `s${stepIndex}_llm_text`;
    }

    generateMemoryStepOutput(stepIndex: string): string {
        return `s${stepIndex}_memory_value`;
    }

    generateNonLLMStepOutput(stepIndex: string): {
        _compiled_text: string;
        _all_fields: string;
        _presigned_urls: string;
        _total_results: string;
    } {
        return {
            _compiled_text: `s${stepIndex}_rag_texts`,
            _all_fields: `s${stepIndex}_rag_items`,
            _presigned_urls: `s${stepIndex}_rag_urls`,
            _total_results: `s${stepIndex}_rag_total`,
        };
    }

    generateAPIStepOutput(stepIndex: string): string {
        return `s${stepIndex}_api_data`;
    }

    generateLoopStepOutput(stepIndex: string): string {
        return `s${stepIndex}_loop_text`;
    }
}

// Example usage
const handler = new OpCodeBuilderHandler();
export default handler;
